import { isMarine, isUncollectedVin, normalizeVin } from './assetType.js';
import { marineAttrsOf } from './marineService.js';

/**
 * The identifier to show a technician for an asset, and what to call it.
 *
 * A car is identified by its VIN. A craft is identified by its **HIN** — and its
 * `vin` field holds a synthetic `BT`-prefixed placeholder that is an internal
 * join key, never something to show anyone.
 *
 * `isUncollectedVin` already hides that placeholder, so a boat currently shows a
 * technician NO identifier at all where a car shows a VIN. At a marina with
 * several similar craft that is the one thing that would tell them they are at
 * the right boat.
 *
 * **The HIN is captured AT check-in**, so before check-in there is genuinely
 * nothing to show. Returning null then is correct — it is not a missing-data
 * bug, it is a not-yet-collected state, and the alternative is a label with
 * nothing beside it.
 *
 * Returns `null` rather than a partial result so callers can render nothing
 * without having to know any of the above.
 *
 * Imports only zero-import modules, so this stays harness-testable
 * (`scripts/test/assetIdentifier.test.mjs`).
 *
 * @param {object} source anything carrying a vin/VIN and, for a craft, marine
 *   attributes either flat or nested — a request, a route stop, a service row
 * @returns {{label: string, value: string}|null}
 */
export function assetIdentifier(source) {
  const s = source || {};
  const rawVin = s.vin ?? s.VIN ?? s.Vin ?? s.vehicle_vin;

  if (isMarine(rawVin)) {
    const hin = marineAttrsOf(s).hin;
    const value = normalizeVin(hin);
    return value ? { label: 'HIN', value } : null;
  }

  // Covers the 'NV' not-collected placeholder as well as anything blank.
  if (!rawVin || isUncollectedVin(rawVin)) return null;
  return { label: 'VIN', value: normalizeVin(rawVin) };
}
